import axios from 'axios';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { clearAllStorage } from '../utils/authStorage';

const API_BASE_URL = `https://${
  import.meta.env['VITE_API_DOMAIN_NAME'] ||
  import.meta.env['API_DOMAIN_NAME'] ||
  'apis.qa.loopay.com.pl'
}`;

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Email of the currently authenticated user, cached from the most recent
 * successful auth session. Written in the request interceptor (before any
 * error path) so it is always available in handleLogout() even if Amplify
 * has already cleared the local session by the time an error is processed.
 */
let cachedUserEmail = '';

const isTestAccount = () => cachedUserEmail.startsWith('pozytron.test');

apiClient.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Cache email now — error handlers cannot reliably re-fetch it after a
    // globalSignOut has cleared the Amplify session
    const email = session.tokens?.idToken?.payload?.['email'];
    if (typeof email === 'string' && email) {
      cachedUserEmail = email;
    }
  } catch (error) {
    console.warn('No auth - testing mode', error);
  }
  return config;
});

/**
 * Module-scope promise that deduplicates concurrent token refreshes.
 * If multiple requests fail with 401 simultaneously, only one forceRefresh
 * call is made to Cognito — all callers await the same promise.
 */
let refreshTokenPromise: Promise<string | null> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;

    if (status === 401 && !error.config._retry) {
      error.config._retry = true;

      try {
        if (!refreshTokenPromise) {
          refreshTokenPromise = fetchAuthSession({ forceRefresh: true })
            .then((session) => session.tokens?.idToken?.toString() ?? null)
            .finally(() => {
              refreshTokenPromise = null;
            });
        }

        const token = await refreshTokenPromise;

        if (token && status === 401) {
          error.config.headers.Authorization = `Bearer ${token}`;
          return apiClient(error.config);
        } else {
          await handleLogout();
          return new Promise(() => {});
        }
      } catch (refreshError) {
        const errorMessage =
          refreshError instanceof Error
            ? refreshError.message
            : 'Token refresh failed';

        if (
          errorMessage.includes('PreTokenGeneration failed') ||
          errorMessage.includes('dezaktywowane') ||
          errorMessage.includes('deactivated') ||
          errorMessage.includes('User is disabled')
        ) {
          await handleLogout('ACCOUNT_DEACTIVATED');
          return new Promise(() => {});
        } else if (
          errorMessage.includes('NotAuthorizedException') ||
          errorMessage.includes('Refresh Token has expired') ||
          errorMessage.includes('Token has been revoked') ||
          errorMessage.includes('Invalid Refresh Token') ||
          errorMessage.includes('UserNotFoundException') ||
          errorMessage.includes('User does not exist')
        ) {
          await handleLogout();
          return new Promise(() => {});
        }
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Handle user logout with optional deactivation reason.
 * Disabled for test accounts (pozytron.test+) so that a globalSignOut
 * triggered by one parallel E2E scenario does not force-logout other
 * concurrently running scenarios that share the same Cognito user pool.
 */
const handleLogout = async (reason?: string) => {
  if (isTestAccount()) {
    return;
  }

  try {
    await signOut();
  } catch (error) {
    console.warn('Error during sign out:', error);
  }

  await clearAllStorage();

  // Set logout sync — written after clear so other tabs receive the storage event
  try {
    localStorage.setItem('logout-event', Date.now().toString());
    if (reason) {
      localStorage.setItem('logout-reason', reason);
    }
  } catch (e) {
    console.warn('Failed to set logout sync:', e);
  }

  window.location.href = '/login';
};
